<template>
	<div class="main admin-courses-edit">
		<FormWrapper :action="formAction" :method="formMethod">
			<div class="title-bar">
				<h1 class="mb-0">{{ course.id ? "Edit" : "Add New" }} Course</h1>
				<a class="btn-back" :href="route('admin.courses.index')">
					<span class="icon icon-arrow-left"></span>
					<span class="text">Back to List</span>
				</a>
			</div>
			<div class="row no-gutters py-4">
				<div class="col-12 col-lg-8 col-xl-9">
					<div class="row no-gutters px-3 pl-lg-4 pr-lg-0 pl-xl-5">
						<div class="col-12">
							<transition name="fade">
								<Alert v-if="hasAlert" :alert="alert"/>
							</transition>
						</div>
						<div class="col-12 mb-4">
							<div class="container">
								<h3 class="text-center mb-3">
									What type of course should be used?
								</h3>
								<EditCourseType
									:course="course"
									:types="types"
									v-model="courseType"
								/>
							</div>
						</div>
						<div class="col-12 mb-4">
							<div class="container">
								<h3 class="text-center mb-3">
									What is the name of the course?
								</h3>
								<EditCourseName v-if="!course.id" :value="course.name"/>
								<Field v-else type="text" name="name" :value="course.name"/>
								<FindSimilarCourses
									v-if="course.id && course.is_requested && !course.is_active"
									:course="course"
								/>
							</div>
						</div>

						<div class="col-12">
							<hr/>
							<EditorGroupToggle
								label="Course Settings"
								target="toggleGroupSettings"
								class="mb-4"
								:collapsed="false"
							/>
							<div id="toggleGroupSettings" class="collapse show row">
								<div class="col-12 mb-3">
									<Field
										type="text"
										name="_keywords"
										label="Keywords"
										:value="course.keywords"
									/>
								</div>
								<div class="col-12 col-xl-6 col-xxl-3 mb-3">
									<Field
										type="text"
										name="created_by_name"
										label="Admin Contact"
										:value="createdByName"
										:disabled="true"
									/>
								</div>
								<div class="col-12 col-xl-6 col-xxl-3 mb-3">
									<Field
										type="text"
										name="number"
										label="Course Number"
										:value="course.number"
									/>
								</div>
								<div class="col-12 col-xl-6 col-xxl-3 mb-3">
									<Field
										type="text"
										name="length"
										label="Course Length"
										:value="course['length']"
									/>
								</div>
								<div class="col-12 col-xl-6 col-xxl-3 mb-3">
									<Field
										type="text"
										name="duration"
										label="Duration (in hours)"
										:value="course.duration"
									/>
								</div>
								<div class="col-12 col-xl-6 mb-3">
									<Field
										type="select"
										name="owner_type"
										label="Ownership Type"
										v-model="ownerType"
										:options="ownerTypes"
										:hideSelectEmptyOption="true"
									/>
								</div>
								<div class="col-12 col-xl-6 mb-3">
									<div class="owned-by-wrap">
										<icon
											v-if="showOwnedByLoadingIcon"
											icon="loading"
											class="loading-icon"
										/>
										<Field
											v-if="ownedByOptions.length > 0"
											type="select"
											name="owned_by"
											:label="ownedByLabel"
											v-model="ownedBy"
											:options="ownedByOptions"
											:hideSelectEmptyOption="true"
										/>
									</div>
								</div>
								<div class="col-12 col-xl-6 mb-3 pt-3">
									<Field
										type="checkbox"
										name="managers_only"
										id="cb_managers_only"
										label="Only distributor and factory users may enroll in this course"
										v-model="managersOnly"
										:checkbox-value="1"
									/>
                  <div class="mt-2">
                    <Field
                        type="checkbox"
                        name="factory_only"
                        id="cb_factory_only"
                        label="Only factory users may enroll in this course"
                        :value="!!course.factory_only"
                        :checkbox-value="1"
                    />
                  </div>
                  <div class="mt-2">
                    <Field
                        type="checkbox"
                        name="is_featured"
                        id="cb_is_featured"
                        label="Feature this course above other listings"
                        :value="!!course.is_featured"
                        :checkbox-value="1"
                    />
                  </div>
								</div>
								<div class="col-12 col-xl-6 mb-3 pt-3">
									<Field
										v-if="ownerType == 'DISTRIBUTOR'"
										type="checkbox"
										name="is_private"
										id="cb_is_private"
										label="This course is PRIVATE"
										:value="!!course.is_private"
										:checkbox-value="1"
									/>
								</div>
								<div class="col-12 col-xl-3 mb-3">
									<Field
										type="text"
										name="nate_id"
										label="NATE Course ID"
										:value="course.nate_id"
									/>
								</div>
								<div class="col-12 col-xl-3 mb-3 pt-4">
									<Field
										type="checkbox"
										class="pt-2"
										name="is_nate"
										id="cb_is_nate"
										label="This course is NATE Certified"
										v-model="isNate"
										:checkbox-value="1"
									/>
								</div>
								<div class="col-12 col-xl-3 mb-3">
									<Field
										v-if="isNate"
										type="text"
										name="nate_cost"
										label="NATE Cost"
										:value="course.nate_cost"
									/>
								</div>
								<div class="col-12 col-xl-3 mb-3">
									<Field
										v-if="isNate"
										type="text"
										name="nate_hours"
										label="NATE Hours"
										:value="course.nate_hours"
									/>
								</div>
                <div class="col-12 col-xl-3 mb-3">
                  <Field
                      type="text"
                      name="pdh_id"
                      label="PDH Course ID"
                      :value="course.pdh_id"
                  />
                </div>
							</div>
						</div>

						<EditClassroomFields
							v-if="isClassroom"
							:course="course"
              :courses="courses"
							:owner-type="ownerType"
							:cost-types="costTypes"
							:invoice-types="invoiceTypes.CLASSROOM"
							:managers-only="managersOnly"
						/>

						<EditOnlineFields
							v-if="isOnline"
							:course="course"
							:owner-type="ownerType"
							:series="series"
							:invoice-types="invoiceTypes.ONLINE"
							:managers-only="managersOnly"
						/>

						<EditTrainingModule
							v-if="isOnline"
							:course="course"
							:training-types="trainingTypes"
							:module-types="moduleTypes"
							:external-vendors="externalVendors"
							:completion-verbs="completionVerbs"
						/>

            <template v-if="isOnline">
              <div class="col-12 col-xl-6 mb-3">
                <Field
                    type="checkbox"
                    name="meta[is_ilg]"
                    id="cb_is_ilg"
                    label="This is an ILG course"
                    v-model="course.is_ilg"
                    :checkbox-value="1"
                />
              </div>
            </template>

						<div class="col-12">
							<hr/>
							<EditorGroupToggle
								label="Brands, Categories, Topics & Tags"
								target="toggleGroupFilters"
								class="mb-4"
								:collapsed="false"
							/>
							<div id="toggleGroupFilters" class="collapse show row no-gutters">
								<div class="col-12 mb-3 col-xl-6 pr-xl-3">
									<MultiAssignField
										type="select"
										label="Brand"
										name="brands"
										:options="brands"
										:value="course._brands"
									/>
								</div>
								<div class="col-12 mb-3 col-xl-6 pl-xl-3">
									<MultiAssignField
										type="select"
										label="Tags"
										name="tags"
										:options="tagOptions"
										:value="course._tags"
									/>
								</div>
								<div class="col-12 mb-3 col-xl-6 pr-xl-3">
									<EditCategories
										:categories="categories"
										v-model="courseCategories"
									/>
								</div>
								<div class="col-12 mb-3 col-xl-6 pl-xl-3">
									<MultiAssignField
										type="select"
										label="Topics"
										name="topics"
										:options="topicOptions"
										:value="course._topics"
									/>
								</div>
							</div>
						</div>

						<div class="col-12">
							<hr/>
							<EditorGroupToggle
								label="Surveys & Tests"
								target="toggleGroupForms"
								class="mb-4"
								:collapsed="false"
							/>
							<div id="toggleGroupForms" class="collapse show row no-gutters">
								<div class="col-12 mb-3 col-xl-6 pr-xl-3">
									<Field
										type="select"
										label="Survey"
										name="survey_id"
										:options="surveys"
										:value="course.survey_id"
									/>
									<div v-if="isClassroom" class="help-text">
										The default survey to assign to events for this course
									</div>
								</div>
								<div class="col-12 mb-3 col-xl-6 pl-xl-3">
									<Field
										type="select"
										label="Test"
										name="test_id"
										:options="tests"
										:value="course.test_id"
										:disabled="isOnline"
									/>
									<div v-if="isClassroom" class="help-text">
										The default test to assign to events for this course
									</div>
								</div>
							</div>
						</div>

						<div class="col-12">
							<hr/>
							<EditorGroupToggle
								label="Additional Content"
								target="toggleGroupContent"
								class="mb-4"
							/>
							<div id="toggleGroupContent" class="collapse row no-gutters">
								<div class="col-12 mb-3">
									<h4 class="text-primary text-uppercase">Description</h4>
									<HtmlField name="description" :value="course.description"/>
								</div>
								<div class="col-12 mb-3 col-xl-6 pr-xl-3">
									<h4 class="text-primary text-uppercase mb-3">
										Featured Image
									</h4>
									<AssetField
										name="image_id"
										:value="course.image_id"
										:types="['image']"
									/>
								</div>
								<div class="col-12 mb-3 col-xl-6 pl-xl-3">
									<h4 class="text-primary text-uppercase mb-3">Course Flyer</h4>
									<AssetField
										name="meta[course_flyer]"
										:value="course.course_flyer"
										:types="['document']"
										show-view-button
									/>
								</div>
								<div class="col-12 mb-3 col-xl-6 pr-xl-3">
									<h4 class="text-primary text-uppercase mb-3">Intro Video</h4>
									<Field
										type="select"
										name="video_type"
										class="mb-3"
										label="Video Type"
										v-model="videoType"
										:options="videoTypes"
										:hideSelectEmptyOption="true"
									/>
									<Field
										v-if="videoType == 'url'"
										type="text"
										name="video_url"
										:value="course.video_url"
									/>
									<AssetField
										v-else
										name="video_id"
										:value="course.video_id"
										:types="['video']"
									/>
								</div>
								<div class="col-12 mb-3 col-xl-6 pl-xl-3">
									<h4 class="text-primary text-uppercase mb-3">Downloads</h4>
									<Downloads
										:value="course._downloads"
										:visibilities="downloadTypes[courseType] || []"
									/>
								</div>
							</div>
						</div>

						<div class="col-12">
							<hr/>
							<EditorGroupToggle
								label="Administrative Notes & Policies"
								target="toggleGroupNotes"
								class="mb-4"
							/>
							<div id="toggleGroupNotes" class="collapse row no-gutters">
								<div class="col-12 mb-3">
									<HtmlField
										name="meta[admin_notes]"
										label="Admin Notes"
										:value="course.admin_notes"
										:hideAssetIcons="true"
									/>
								</div>
								<div class="col-12 mb-3">
									<HtmlField
										name="meta[cancel_policy]"
										label="Cancellation Policy"
										:value="course.cancel_policy"
										:hideAssetIcons="true"
									/>
								</div>
								<div v-if="isClassroom" class="col-12 mb-3">
									<HtmlField
										name="meta[event_request_notes]"
										label="Special Instructions for Event Requests"
										:value="course.event_request_notes"
										:hideAssetIcons="true"
									/>
								</div>
							</div>
						</div>

						<EditFeedbackFields :course="course"/>
					</div>
				</div>

				<div class="col-12 col-lg-4 col-xl-3 form-button-wrapper">
					<div class="sticky-top">
						<div class="row no-gutters text-center">
							<div class="col-12">
								<h4 class="text-uppercase text-center">Status</h4>
								<SwitchField
									name="is_active"
									on-label="Active"
									off-label="Inactive"
									:value="!!course.is_active ? 1 : 0"
								/>
								<Field
									type="checkbox"
									class="mt-3"
									name="hide_from_catalog"
									id="cb_hide_from_catalog"
									label="Hide this course from the catalog"
									:value="!!course.hide_from_catalog"
									:checkbox-value="1"
								/>
								<Field
									type="checkbox"
									class="mt-3"
									name="disable_emails"
									id="cb_disable_emails"
									label="Disable notifications for this course"
									:value="!!course.disable_emails"
									:checkbox-value="1"
								/>
								<hr class="w-75"/>
							</div>
							<div class="col-12 pb-3">
								<button type="submit" class="btn btn-success w-50">
									Save Changes
								</button>
							</div>
							<div class="col-12 pb-3">
								<a
									:href="route('admin.courses.index')"
									class="btn btn-danger w-50"
								>Cancel & Exit</a
								>
							</div>
							<template v-if="course.id">
								<div v-if="isClassroom" class="col-12 pb-3">
									<a
										:href="route('admin.courses.trainers', routeParam)"
										class="btn btn-info w-50"
									>Edit Trainers</a
									>
								</div>
								<div v-if="isOnline" class="col-12 pb-3">
									<a
										:href="route('admin.courses.enrollments.index', routeParam)"
										class="btn btn-info w-50"
									>View Enrollments</a
									>
								</div>
								<div class="col-12 pb-3">
									<a
										:href="route('admin.courses.surveys.index', routeParam)"
										class="btn btn-info w-50"
									>View Surveys</a
									>
								</div>
								<div class="col-12 font-size-sm text-center pt-4">
                  <span
                  >Last updated on
                    {{ course.updated_at | moment("MM/DD/YYYY") }}</span
                  >
									<span>at {{ course.updated_at | moment("h:mma") }}</span>
									<span v-if="course.updatedBy">{{
											" " + course.updatedBy.name
										}}</span>
								</div>
							</template>
						</div>
					</div>
				</div>
			</div>
		</FormWrapper>
	</div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import EditCourseType from "./partials/EditCourseType";
import EditCourseName from "./partials/EditCourseName";
import EditClassroomFields from "./partials/EditClassroomFields";
import EditOnlineFields from "./partials/EditOnlineFields";
import EditTrainingModule from "./partials/EditTrainingModule";
import EditCategories from "./partials/EditCategories";
import EditFeedbackFields from "./partials/EditFeedbackFields";
import FindSimilarCourses from './partials/FindSimilarCourses'

export default {
	mixins: [DisplaysAlerts],
	components: {
		FindSimilarCourses,
		EditCourseType,
		EditCourseName,
		EditClassroomFields,
		EditOnlineFields,
		EditTrainingModule,
		EditCategories,
		EditFeedbackFields,
	},
	props: {
		course: {type: Object, required: true},
		types: {type: Array, required: true},
		ownerTypes: {type: Array, required: true},
		costTypes: {type: Array, required: true},
		invoiceTypes: {type: Object, required: true},
		mediaTypes: {type: Array, required: true},
		downloadTypes: {type: Object, required: true},
		trainingTypes: {type: Array, required: true},
		moduleTypes: {type: Array, required: true},
		externalVendors: {type: Array, required: true},
		completionVerbs: {type: Array, required: true},
    courses: {type: Array, default: () => []},
		brands: {type: Array, default: () => []},
		categories: {type: Array, default: () => []},
		topics: {type: Array, default: () => []},
		tags: {type: Array, default: () => []},
		series: {type: Array, default: () => []},
		tests: {type: Array, default: () => []},
		surveys: {type: Array, default: () => []},
	},
	data() {
		return {
			courseType: null,
			ownerType: null,
			ownedBy: null,
			managersOnly: false,
			distributorOwners: [],
			trainerOwners: [],
			showOwnedByLoadingIcon: false,
			isNate: false,
			courseCategories: [],
			videoType: "asset",
			videoTypes: [
				{label: "Asset Library", value: "asset"},
				{label: "URL", value: "url"},
			],
		};
	},
	computed: {
		routeParam() {
			return {course: this.course.id || null};
		},
		formAction() {
			return !this.course.id
				? this.route("admin.courses.store")
				: this.route("admin.courses.update", this.routeParam);
		},
		formMethod() {
			return !this.course.id ? "POST" : "PUT";
		},
		isClassroom() {
			return this.courseType == this.$root.constants.CLASSROOM_COURSE;
		},
		isOnline() {
			return this.courseType == this.$root.constants.ONLINE_COURSE;
		},
		tagOptions() {
			return this.tags.filter((tag) => tag.type == this.courseType);
		},
		topicOptions() {
			return this.topics
				.filter((topic) => {
					return (
						this.courseCategories.filter(
							(item) => item.category_id == topic.category
						).length > 0
					);
				})
				.map((topic) => {
					var category = this.categories.find((c) => c.value == topic.category);
					topic.label += ` (${category.label})`;
					return topic;
				});
		},
		createdByName() {
			return !!this.course.created_by
				? this.course.created_by.name
				: this.$root.user.name;
		},
		ownedByOptions() {
			if (this.ownerType == "DISTRIBUTOR") {
				return this.distributorOwners;
			} else if (this.ownerType == "TRAINER") {
				return this.trainerOwners;
			} else {
				return [];
			}
		},
		ownedByLabel() {
			if (this.ownerType == "DISTRIBUTOR") {
				return "Distributor Company";
			} else if (this.ownerType == "TRAINER") {
				return "Trainer Company";
			} else {
				return "";
			}
		},
	},
	watch: {
		ownerType(val) {
			if (val == "DISTRIBUTOR" && this.distributorOwners.length == 0) {
				this.loadDistributorOwners();
			} else if (val == "TRAINER" && this.trainerOwners.length == 0) {
				this.loadTrainerOwners();
			} else {
				this.showOwnedByLoadingIcon = false;
			}
		},
		managersOnly(val) {
			if (this.managersOnly) {
				this.course.cost = null;
				this.course.fad_cost = null;
			}
		},
	},
	methods: {
		loadDistributorOwners() {
			this.showOwnedByLoadingIcon = true;
			this.axios
				.get(this.route("admin.courses.owners"), {
					params: {owner_type: "DISTRIBUTOR"},
				})
				.then((response) => {
					this.distributorOwners = response.data;
					this.showOwnedByLoadingIcon = false;
				});
		},
		loadTrainerOwners() {
			this.showOwnedByLoadingIcon = true;
			this.axios
				.get(this.route("admin.courses.owners"), {
					params: {owner_type: "TRAINER"},
				})
				.then((response) => {
					this.trainerOwners = response.data;
					this.showOwnedByLoadingIcon = false;
				});
		},
	},
	mounted() {
		this.courseType = this.course.type;
		this.ownerType = this.course.owner_type;
		this.ownedBy = this.course.owned_by;
		this.managersOnly = !!this.course.managers_only;
		this.isNate = !!this.course.is_nate;
		this.courseCategories = this.course._categories || [];
		this.videoType = !!this.course.video_url ? "url" : "asset";
	},
};
</script>
